import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stepper,
    Step,
    StepLabel,
    Box,
    Typography,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Checkbox,
    ToggleButton,
    ToggleButtonGroup,
    FormControlLabel,
    Stack,
    Tooltip,
} from '@mui/material';
import RequestService from '../../services/RequestService';
import { ReactComponent as CoinIcon } from '../../assets/icons/coin.svg';
import { ReactComponent as XpIcon } from '../../assets/icons/xp.svg';
import RewardBadge from '../RewardBadge';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const NewContentDialog = ({ open, onClose, onSuccess, user }) => {
    const aiAvailable = Boolean(user?.companyPlan?.aiAutoReviewEnabled);
    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        type: 'MISSION',
        subType: '',
        attitudeType: '',
        completionLimit: '',
        difficulty: 'EASY',
        repetition: 'ONCE',
        customRepetition: '',
        startDate: '',
        endDate: '',
        isLimitedTime: false,
        isEntireGame: false,
        games: [],
        groups: [],
        exp: 0,
        coin: 0,
        isAutomatic: false,
        allowTextProof: false,
        allowFileProof: false,
        locationRestricted: false,
        aiCorePoints: '',
    });

    const [games, setGames] = useState([]);
    const [groups, setGroups] = useState([]);
    const [error, setError] = useState(null);
    const [isEntireGame, setIsEntireGame] = useState(false);
    const [approvalMode, setApprovalMode] = useState('CONVENTIONAL');

    useEffect(() => {
        if (!open) {
            setFormData({
                title: '',
                description: '',
                type: 'MISSION',
                subType: '',
                attitudeType: '',
                completionLimit: '',
                difficulty: 'EASY',
                repetition: 'ONCE',
                customRepetition: '',
                startDate: '',
                endDate: '',
                isLimitedTime: false,
                isEntireGame: false,
                games: [],
                groups: [],
                exp: 0,
                coin: 0,
                isAutomatic: false,
                allowTextProof: false,
                allowFileProof: false,
                locationRestricted: false,
                iaEnabled: false,
                aiCorePoints: '',
            });
            setActiveStep(0);
            setApprovalMode('CONVENTIONAL');
        }
    }, [open]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const gamesData = await RequestService.getGames(
                    user.company.id
                );
                const groupsData = await RequestService.getGroups(
                    user.company.id
                );

                setGames(Array.isArray(gamesData) ? gamesData : []);
                setGroups(Array.isArray(groupsData) ? groupsData : []);
            } catch (err) {
                setError('Failed to load data.');
                setGames([]);
                setGroups([]);
            }
        };

        fetchData();
    }, [user.company.id]);

    const steps = ['Informações', 'Detalhes', 'Validação', 'Revisão'];

    const handleApprovalToggle = (event, value) => {
        if (value !== null) {
            setApprovalMode(value);
        }
    };

    const handleNext = () => {
        setActiveStep((prevStep) => {
            if (
                prevStep === 1 &&
                formData.type === 'ATTITUDE' &&
                formData.attitudeType === 'NEGATIVE'
            ) {
                return prevStep + 2;
            }
            return prevStep + 1;
        });
    };

    const handleBack = () => {
        setActiveStep((prevStep) => {
            if (
                prevStep === 3 &&
                formData.type === 'ATTITUDE' &&
                formData.attitudeType === 'NEGATIVE'
            ) {
                return prevStep - 2;
            }
            return prevStep - 1;
        });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => {
            const updatedForm = {
                ...prev,
                [name]: type === 'checkbox' ? checked : value,
            };
            updatedForm.exp = calculateXP(updatedForm);
            return updatedForm;
        });
    };

    const handleToggleChange = (name) => (event, value) => {
        if (value !== null) {
            setFormData((prev) => {
                const updatedForm = { ...prev, [name]: value };
                updatedForm.exp = calculateXP(updatedForm);
                return updatedForm;
            });
        }
    };

    const handleToggleAttitudeType = (name) => (event, value) => {
        if (value !== null) {
            setFormData((prev) => {
                const updatedForm = { ...prev, [name]: value };
                return updatedForm;
            });
        }
    };

    const handleSubmit = async () => {
        try {
            const payload = {
                title: formData.title,
                description: formData.description,
                type: formData.type,
                difficultyLevel: formData.difficulty,
                exp: formData.exp,
                coin: formData.coin,
                isAutomatic: formData.isAutomatic,
                allowTextProof: formData.allowTextProof,
                allowFileProof: formData.allowFileProof,
                isLimitedTime: !!formData.startDate && !!formData.endDate,
                startDate: formData.startDate,
                endDate: formData.endDate,
                completionLimit: formData.completionLimit ?? null,
                location: formData.isAutomatic ? 'VALIDATION_REQUIRED' : '',
                companyId: user.company.id,
                subType: formData.subType,
            };

            if (isEntireGame) {
                payload.isEntireGame = true;
                payload.gameIds = formData.games;
            } else {
                payload.isEntireGame = false;
                payload.groupIds = formData.groups;
            }

            if (
                formData.type === 'ATTITUDE' &&
                formData.attitudeType === 'NEGATIVE'
            ) {
                payload.attitudeType = 'NEGATIVE';
                payload.subType = 'NORMAL';
                payload.isLimitedTime = false;
                payload.isAutomatic = false;
            } else if (formData.type === 'ATTITUDE') {
                payload.attitudeType = 'POSITIVE';
            }

            if (aiAvailable && approvalMode === 'AI') {
                payload.iaEnabled = true;
                payload.aiCorePoints = formData.aiCorePoints || '';
            }

            await RequestService.createContent(payload);

            setFormData({
                title: '',
                description: '',
                type: 'MISSION',
                subType: '',
                attitudeType: '',
                completionLimit: '',
                difficulty: 'EASY',
                repetition: 'ONCE',
                customRepetition: '',
                startDate: '',
                endDate: '',
                isLimitedTime: false,
                isEntireGame: false,
                games: [],
                groups: [],
                exp: 0,
                coin: 0,
                isAutomatic: false,
                locationRestricted: false,
                aiCorePoints: '',
            });

            setApprovalMode('CONVENTIONAL');
            setActiveStep(0);

            onSuccess('Content created successfully!');
            onClose();
        } catch (err) {
            setError('Failed to create content.');
        }
    };

    const calculateXP = (formData) => {
        let baseXP = 0;

        switch (formData.difficulty) {
            case 'EASY':
                baseXP = 100;
                break;
            case 'INTERMEDIATE':
                baseXP = 200;
                break;
            case 'HARD':
                baseXP = 300;
                break;
            default:
                baseXP = 0;
        }

        let bonusPercentage = 0;

        if (formData.isLimitedTime === 'true') bonusPercentage += 5;
        if (formData.locationRestricted) bonusPercentage += 10;
        if (formData.isAutomatic === true) bonusPercentage += 5;
        if (formData.isSpecialMission) bonusPercentage += 5;

        const totalXP = Math.round(baseXP * (1 + bonusPercentage / 100));

        return totalXP;
    };

    const handleGameOrGroupSelect = (value) => {
        setIsEntireGame(value);
    };

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        <TextField
                            label="Nome"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            label="Descrição"
                            name="description"
                            value={formData.description}
                            onChange={(e) => {
                                if (e.target.value.length <= 300) {
                                    setFormData((prev) => ({
                                        ...prev,
                                        description: e.target.value,
                                    }));
                                }
                            }}
                            fullWidth
                            multiline
                            rows={3}
                            helperText={`${formData.description.length}/300`}
                            FormHelperTextProps={{
                                sx: {
                                    color:
                                        formData.description.length === 300
                                            ? 'red'
                                            : 'gray',
                                },
                            }}
                        />
                        <Typography variant="body1">Formato</Typography>
                        <ToggleButtonGroup
                            value={formData.type}
                            fullWidth
                            exclusive
                            onChange={handleToggleChange('type')}
                            color="primary"
                        >
                            <ToggleButton value="MISSION">Missão</ToggleButton>
                            <ToggleButton value="ATTITUDE">
                                Atitude
                            </ToggleButton>
                        </ToggleButtonGroup>
                        {formData.type === 'ATTITUDE' && (
                            <>
                                <Typography variant="body1">
                                    Essa atitude é uma atitude positiva ou
                                    negativa?
                                </Typography>
                                <ToggleButtonGroup
                                    value={formData.attitudeType}
                                    exclusive
                                    fullWidth
                                    onChange={handleToggleChange(
                                        'attitudeType'
                                    )}
                                    color="primary"
                                >
                                    <ToggleButton value="POSITIVE">
                                        Positiva
                                    </ToggleButton>
                                    <ToggleButton value="NEGATIVE">
                                        Negativa
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </>
                        )}
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Typography>Nível de dificuldade</Typography>
                            <Tooltip title="O nível de dificuldade afeta a quantidade de experiência recebida pelo jogador. Sendo 100, 200 e 300 de experiência, respectivamente.">
                                <HelpOutlineIcon
                                    color="primary"
                                    sx={{
                                        width: '18px',
                                        height: '18px',
                                    }}
                                />
                            </Tooltip>
                        </Stack>
                        <ToggleButtonGroup
                            value={formData.difficulty}
                            exclusive
                            fullWidth
                            onChange={handleToggleChange('difficulty')}
                            color="primary"
                        >
                            <ToggleButton value="EASY">Fácil</ToggleButton>
                            <ToggleButton value="INTERMEDIATE">
                                Normal
                            </ToggleButton>
                            <ToggleButton value="HARD">Difícil</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                );
            case 1:
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        {formData.attitudeType !== 'NEGATIVE' && (
                            <>
                                <ToggleButtonGroup
                                    value={formData.subType}
                                    fullWidth
                                    exclusive
                                    onChange={handleToggleChange('subType')}
                                    color="primary"
                                >
                                    <ToggleButton value="UNIQUE">
                                        Uma vez
                                    </ToggleButton>
                                    <ToggleButton value="LIMITED">
                                        Personalizado
                                    </ToggleButton>
                                    <ToggleButton value="NORMAL">
                                        Sem Limite
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                {formData.subType === 'LIMITED' && (
                                    <TextField
                                        label="Insira a quantidade"
                                        name="completionLimit"
                                        type="number"
                                        value={formData.completionLimit}
                                        onChange={handleChange}
                                        fullWidth
                                    />
                                )}

                                <Typography variant="body1">
                                    Data de validade
                                </Typography>
                                <ToggleButtonGroup
                                    value={formData.isLimitedTime}
                                    fullWidth
                                    exclusive
                                    onChange={handleToggleChange(
                                        'isLimitedTime'
                                    )}
                                    color="primary"
                                >
                                    <ToggleButton value={false}>
                                        Sem validade
                                    </ToggleButton>
                                    <ToggleButton value={true}>
                                        Selecionar dia
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                {formData.isLimitedTime === true && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2,
                                        }}
                                    >
                                        <TextField
                                            helperText="Data e Hora Inicial"
                                            name="startDate"
                                            type="datetime-local"
                                            value={formData.startDate}
                                            onChange={handleChange}
                                            fullWidth
                                        />
                                        <TextField
                                            helperText="Data e Hora Final"
                                            name="endDate"
                                            type="datetime-local"
                                            value={formData.endDate}
                                            onChange={handleChange}
                                            fullWidth
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                        <Typography variant="body1">
                            Esse conteúdo será disponibilizado para:
                        </Typography>
                        <ToggleButtonGroup
                            value={isEntireGame}
                            fullWidth
                            exclusive
                            onChange={(event, newValue) => {
                                if (newValue !== null) {
                                    setIsEntireGame(newValue);
                                }
                            }}
                            color="primary"
                        >
                            <ToggleButton value={true}>
                                Jogos Selecionados
                            </ToggleButton>
                            <ToggleButton value={false}>
                                Grupos específicos
                            </ToggleButton>
                        </ToggleButtonGroup>
                        {isEntireGame === true && (
                            <FormControl>
                                <InputLabel>Jogos</InputLabel>
                                <Select
                                    name="games"
                                    value={formData.games}
                                    onChange={handleChange}
                                    multiple
                                >
                                    {(games || []).map((game) => (
                                        <MenuItem key={game.id} value={game.id}>
                                            {game.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        {isEntireGame === false && (
                            <FormControl>
                                <InputLabel>Grupos</InputLabel>
                                <Select
                                    name="groups"
                                    value={formData.groups}
                                    onChange={handleChange}
                                    multiple
                                >
                                    {(groups || []).map((group) => (
                                        <MenuItem
                                            key={group.id}
                                            value={group.id}
                                        >
                                            {group.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <Stack direction="row" justifyContent="space-between">
                            <Stack direction="row" gap={1}>
                                <CoinIcon />
                                <TextField
                                    label={
                                        formData.type === 'ATTITUDE' &&
                                        formData.attitudeType === 'NEGATIVE'
                                            ? 'Penalidade (Moedas)'
                                            : 'Recompensa (Moedas)'
                                    }
                                    name="coin"
                                    type="number"
                                    value={formData.coin}
                                    onChange={handleChange}
                                    fullWidth
                                />
                            </Stack>
                        </Stack>
                    </Box>
                );
            case 2:
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        {aiAvailable && (
                            <FormControl>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <Typography>Aprovação</Typography>
                                    <Tooltip title="A aprovação convencional exigirá a aprovação manual de cada solicitação de conclusão. A Aprovação com a Gio utilizará a Gio, nossa Inteligência Artificial, para validar automaticamente as solicitações. Caso ela não consiga validar, ela solicitará aprovação manual.">
                                        <HelpOutlineIcon
                                            color="primary"
                                            sx={{
                                                width: '18px',
                                                height: '18px',
                                            }}
                                        />
                                    </Tooltip>
                                </Stack>
                                <ToggleButtonGroup
                                    value={approvalMode}
                                    fullWidth
                                    exclusive
                                    onChange={handleApprovalToggle}
                                    color="primary"
                                >
                                    <ToggleButton value="CONVENTIONAL">
                                        Convencional
                                    </ToggleButton>
                                    <ToggleButton value="AI">
                                        Aprovação com Gio
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </FormControl>
                        )}

                        {aiAvailable && approvalMode === 'AI' ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <Typography>
                                        O que a Gio deve procurar?
                                    </Typography>
                                    <Tooltip title="O texto deve descrever à nossa IA o que ela deve procurar. Exemplo: A foto deve conter uma tela do Instagram aberta onde o usuário esteja seguindo o perfil GameUP!">
                                        <HelpOutlineIcon
                                            color="primary"
                                            sx={{
                                                width: '18px',
                                                height: '18px',
                                            }}
                                        />
                                    </Tooltip>
                                </Stack>
                                <TextField
                                    label="Validação"
                                    name="aiCorePoints"
                                    value={formData.aiCorePoints}
                                    onChange={handleChange}
                                    fullWidth
                                    helperText="Descreva para nossa IA o que ela deve procurar e analisar para que a missão seja validada ou não."
                                />
                                <Box>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="allowTextProof"
                                                checked={
                                                    formData.allowTextProof ||
                                                    false
                                                }
                                                onChange={handleChange}
                                            />
                                        }
                                        label="Obrigatória comprovação por texto"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="allowFileProof"
                                                checked={
                                                    formData.allowFileProof ||
                                                    false
                                                }
                                                onChange={handleChange}
                                            />
                                        }
                                        label="Obrigatória comprovação através de envio de arquivo"
                                    />
                                </Box>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                }}
                            >
                                <FormControl>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        gap={1}
                                        sx={{ my: 1 }}
                                    >
                                        <Typography variant="body1">
                                            Método de Validação
                                        </Typography>
                                        <Tooltip title="O método de validação automático irá aprovar automaticamente todas as solicitações de conclusão desta missão desde que o jogador esteja, pessoalmente, nas dependências da escola.">
                                            <HelpOutlineIcon
                                                color="primary"
                                                sx={{
                                                    width: '18px',
                                                    height: '18px',
                                                }}
                                            />
                                        </Tooltip>
                                    </Stack>
                                    <ToggleButtonGroup
                                        value={formData.isAutomatic}
                                        fullWidth
                                        exclusive
                                        onChange={(event, value) => {
                                            if (value !== null) {
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    isAutomatic: value,
                                                }));
                                            }
                                        }}
                                        color="primary"
                                    >
                                        <ToggleButton value={false}>
                                            Manual
                                        </ToggleButton>
                                        <ToggleButton value={true}>
                                            Automático
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </FormControl>

                                {!formData.isAutomatic && (
                                    <Box>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="allowTextProof"
                                                    checked={
                                                        formData.allowTextProof ||
                                                        false
                                                    }
                                                    onChange={handleChange}
                                                />
                                            }
                                            label="Obrigatória comprovação por texto"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="allowFileProof"
                                                    checked={
                                                        formData.allowFileProof ||
                                                        false
                                                    }
                                                    onChange={handleChange}
                                                />
                                            }
                                            label="Obrigatória comprovação através de envio de arquivo"
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                );
            case 3:
                return (
                    <Box>
                        <Typography variant="body2">
                            <strong>Título:</strong> {formData.title}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Descrição:</strong> {formData.description}
                        </Typography>
                        <Stack gap={1} justifyContent="space-between">
                            <RewardBadge
                                icon={
                                    <CoinIcon
                                        style={{
                                            color: '#134F99',
                                            fontSize: '24px',
                                        }}
                                    />
                                }
                                value={formData.coin}
                            />
                            <RewardBadge
                                icon={
                                    <XpIcon
                                        style={{
                                            color: '#134F99',
                                            fontSize: '24px',
                                        }}
                                    />
                                }
                                value={formData.exp}
                            />
                        </Stack>
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Nova Missão</DialogTitle>
            <DialogContent>
                <Stepper
                    activeStep={activeStep}
                    alternativeLabel
                    sx={{ marginBottom: '36px' }}
                >
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                {renderStepContent(activeStep)}
            </DialogContent>
            <DialogActions>
                {activeStep > 0 && (
                    <Button onClick={handleBack} variant="outlined">
                        Voltar
                    </Button>
                )}
                {activeStep < steps.length - 1 && (
                    <Button onClick={handleNext} variant="contained">
                        Próximo
                    </Button>
                )}
                {activeStep === steps.length - 1 && (
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                    >
                        Criar Missão
                    </Button>
                )}
                <Button onClick={onClose} variant="outlined">
                    Cancelar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NewContentDialog;
